import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { FilterBar } from '@lms/ui';
import {
  FileText,
  Video,
  Headphones,
  CheckCircle2,
  Loader2,
  MessageSquare,
  HelpCircle,
  Globe,
  ArrowRight,
  Search,
  X,
} from 'lucide-react';
import { useMaterials } from '../hooks/useMaterials';
import { useEnrollments } from '../hooks/useEnrollments';
import { useAuthStore } from '../store/auth';
import type { Material } from '@lms/api-client';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';

type MaterialFilter = 'all' | 'VIDEO' | 'AUDIO' | 'DOCUMENT' | 'QUIZ' | 'REFLECTION' | 'completed';

export function Materials() {
  const [filter, setFilter] = useState<MaterialFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuthStore();
  const {
    data: materials,
    isLoading: materialsLoading,
    error: materialsError,
    refetch,
  } = useMaterials();
  const { data: enrollments, isLoading: enrollmentsLoading } = useEnrollments({ userId: user?.id });
  const { t } = useLanguage();

  const completions = useMemo(
    () => enrollments?.flatMap(e => e.completions?.map(c => c.materialId) || []) || [],
    [enrollments]
  );

  // Follow curriculum hierarchy instead of the global material.order from the list endpoint.
  const curriculumOrder = useMemo(() => {
    const positions = new Map<string, number>();
    for (const enrollment of enrollments || []) {
      for (const level of enrollment.program.levels || []) {
        for (const stage of level.stages || []) {
          for (const subject of stage.subjects || []) {
            for (const session of subject.sessions || []) {
              for (const material of session.materials || []) {
                if (!positions.has(material.id)) positions.set(material.id, positions.size);
              }
            }
          }
        }
        for (const flow of level.flows || []) {
          for (const material of flow.materials || []) {
            if (!positions.has(material.id)) positions.set(material.id, positions.size);
          }
        }
      }
      const rootFlows =
        (
          enrollment.program as typeof enrollment.program & {
            flows?: Array<{ materials?: Array<{ id: string }> }>;
          }
        ).flows || [];
      for (const flow of rootFlows) {
        for (const material of flow.materials || []) {
          if (!positions.has(material.id)) positions.set(material.id, positions.size);
        }
      }
    }
    return positions;
  }, [enrollments]);

  const orderedMaterials = useMemo(
    () =>
      [...(materials || [])].sort(
        (left, right) =>
          (curriculumOrder.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
            (curriculumOrder.get(right.id) ?? Number.MAX_SAFE_INTEGER) || left.order - right.order
      ),
    [curriculumOrder, materials]
  );

  const filtered = useMemo(
    () =>
      orderedMaterials.filter(m => {
        const matchesFilter =
          filter === 'all' ||
          (filter === 'completed'
            ? completions.includes(m.id)
            : filter === 'AUDIO'
              ? m.type === 'AUDIO' || m.type === 'DISCUSSION'
              : m.type === filter);
        const normalizedQuery = searchQuery.trim().toLowerCase();
        const matchesSearch =
          !normalizedQuery ||
          m.title.toLowerCase().includes(normalizedQuery) ||
          m.description?.toLowerCase().includes(normalizedQuery);
        return matchesFilter && matchesSearch;
      }),
    [completions, filter, orderedMaterials, searchQuery]
  );

  const nextMaterial = useMemo(
    () => orderedMaterials.find(material => !completions.includes(material.id)),
    [completions, orderedMaterials]
  );

  const groupedMaterials = filtered.reduce(
    (acc, m) => {
      const subject = m.session?.subject;
      const programName = enrollments?.find(
        enrollment => enrollment.programId === subject?.stage?.level?.programId
      )?.program.name;
      const groupId = subject
        ? `subject:${subject.id}`
        : m.flow?.id
          ? `flow:${m.flow.id}`
          : 'general';
      const groupName = subject
        ? `${programName ? `${programName} • ` : ''}${subject.stage?.stageCode ? `Tahap ${subject.stage.stageCode} • ` : ''}${subject.name}`
        : m.flow?.name || t(copy.common.generalFallback);
      if (!acc[groupId]) acc[groupId] = { name: groupName, items: [] };
      acc[groupId].items.push(m);
      return acc;
    },
    {} as Record<string, { name: string; items: Material[] }>
  );

  if (materialsLoading || enrollmentsLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-sm text-on-surface-variant font-body">
          {t(copy.materials.loadingLibrary)}
        </p>
      </div>
    );
  }

  const filterOptions = [
    {
      value: 'all' as const,
      label: t(copy.materials.filters.all),
      count: materials?.length || 0,
    },
    {
      value: 'VIDEO' as const,
      label: t(copy.materials.filters.video),
      count: materials?.filter(m => m.type === 'VIDEO').length || 0,
    },
    {
      value: 'AUDIO' as const,
      label: t(copy.materials.filters.audio),
      count: materials?.filter(m => m.type === 'AUDIO' || m.type === 'DISCUSSION').length || 0,
    },
    {
      value: 'DOCUMENT' as const,
      label: t(copy.materials.filters.document),
      count: materials?.filter(m => m.type === 'DOCUMENT').length || 0,
    },
    {
      value: 'QUIZ' as const,
      label: t(copy.materials.filters.quiz),
      count: materials?.filter(m => m.type === 'QUIZ').length || 0,
    },
    {
      value: 'completed' as const,
      label: t(copy.materials.filters.completed),
      count: completions.length,
    },
  ];

  const getIcon = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return <Video size={20} />;
      case 'AUDIO':
      case 'DISCUSSION':
        return <Headphones size={20} />;
      case 'QUIZ':
        return <HelpCircle size={20} />;
      case 'REFLECTION':
        return <MessageSquare size={20} />;
      default:
        return <FileText size={20} />;
    }
  };

  if (materialsError) {
    return (
      <div
        role="alert"
        className="flex min-h-64 flex-col items-center justify-center gap-4 rounded-3xl bg-surface-container-low p-6 text-center"
      >
        <p className="m-0 text-sm text-on-surface">{t(copy.materials.loadError)}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="min-h-11 rounded-lg bg-primary px-5 py-2 text-sm font-bold text-on-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
        >
          {t(copy.materials.retry)}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      <section className="grid grid-cols-1 gap-6 rounded-[2rem] bg-surface-container-low p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0">
          <h1 className="m-0 text-3xl font-headline font-bold tracking-tight text-on-surface sm:text-4xl">
            <Bilingual {...copy.materials.title} />
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
            <Bilingual {...copy.materials.subtitle} />
          </p>
          <p className="mt-4 text-xs font-label font-bold uppercase tracking-wider text-primary">
            {t(copy.materials.materialsCount).replace('{count}', String(filtered?.length || 0))}
          </p>
        </div>

        {nextMaterial && (
          <Link
            to={`/materials/${nextMaterial.id}`}
            className="group flex min-w-0 flex-col justify-between rounded-2xl bg-surface-container-lowest p-5 text-on-surface no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
          >
            <div className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary-container text-on-secondary-container">
                {getIcon(nextMaterial.type)}
              </div>
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                <Bilingual {...copy.materials.nextUp} />
              </p>
            </div>
            <div className="mt-5 min-w-0">
              <h2 className="m-0 line-clamp-2 text-lg font-headline font-bold leading-snug group-hover:text-primary">
                {nextMaterial.title}
              </h2>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary">
                <Bilingual {...copy.materials.continueStudy} /> <ArrowRight size={14} />
              </span>
            </div>
          </Link>
        )}
      </section>

      <div className="space-y-4 rounded-3xl bg-surface-container-lowest p-4 shadow-level1 sm:p-5">
        <div className="relative">
          <label
            className="mb-2 block text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant"
            htmlFor="material-search"
          >
            <Bilingual {...copy.materials.searchPlaceholder} />
          </label>
          <Search
            size={18}
            className="pointer-events-none absolute left-4 top-[calc(50%+0.75rem)] -translate-y-1/2 text-on-surface-variant"
            aria-hidden="true"
          />
          <input
            id="material-search"
            type="search"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder={t(copy.materials.searchPlaceholder)}
            className="h-12 w-full rounded-xl bg-surface-container-low py-3 pl-11 pr-11 text-sm text-on-surface outline-none transition-colors placeholder:text-on-surface-variant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-[calc(50%+0.75rem)] flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
              aria-label={t(copy.materials.clearSearch)}
            >
              <X size={16} />
            </button>
          )}
        </div>
        <FilterBar
          options={filterOptions}
          value={filter}
          onChange={v => setFilter(v as MaterialFilter)}
        />
      </div>

      <div className="space-y-10">
        {groupedMaterials &&
          Object.entries(groupedMaterials).map(([groupId, group]) => (
            <div key={groupId} className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="h-8 w-1 rounded-full bg-secondary" />
                <h2 className="text-xs font-label font-black uppercase tracking-[0.18em] text-on-surface-variant">
                  {group.name}
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 sm:gap-6">
                {group.items.map(material => {
                  const isCompleted = completions.includes(material.id);

                  return (
                    <Link
                      key={material.id}
                      to={`/materials/${material.id}`}
                      className="group flex flex-col overflow-hidden rounded-2xl bg-surface-container-lowest p-6 no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div className="p-3 bg-primary/5 rounded-xl text-primary group-hover:text-on-primary group-hover:bg-primary transition-all">
                          {getIcon(material.type)}
                        </div>
                        {isCompleted && <CheckCircle2 size={18} className="text-success" />}
                      </div>

                      <div className="flex-1">
                        <h3 className="text-lg font-headline font-semibold text-on-surface mb-2 group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                          {material.title}
                        </h3>

                        <p className="text-xs text-on-surface-variant font-body mb-4 line-clamp-3 leading-relaxed">
                          {material.description || t(copy.common.noDescription)}
                        </p>
                      </div>

                      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 rounded-xl bg-surface-container-low p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
                            {t(
                              copy.materials.types[
                                material.type.toLowerCase() as keyof typeof copy.materials.types
                              ]
                            )}{' '}
                            {material.duration
                              ? `• ${material.duration} ${t(copy.common.minutes).toUpperCase()}`
                              : ''}
                          </span>
                          {material.meetingUrl && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[8px] font-bold">
                              <Globe size={10} />
                              <span>{t(copy.dashboard.liveSessionLabel)}</span>
                            </span>
                          )}
                        </div>
                        {isCompleted && (
                          <span className="text-[10px] font-label font-bold text-success uppercase tracking-widest">
                            <Bilingual {...copy.materialDetail.completed} />
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      {(!filtered || filtered.length === 0) && (
        <div className="rounded-3xl bg-surface-container-low py-24 text-center">
          <FileText size={48} className="mx-auto mb-4 text-on-surface/10" />
          <p className="text-on-surface-variant font-body italic">
            {t(copy.materials.noMaterialsFound)}
          </p>
        </div>
      )}
    </div>
  );
}
